import { usePostHog } from "@posthog/react";
import { useAppStore } from "@src/store";
import postscribe from "postscribe";
import { type FC, memo, useEffect, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaUniversalAccess } from "react-icons/fa6";

const Form: FC = memo(() => {
  const posthog = usePostHog();
  const showForm = useAppStore((state) => state.showForm);
  const setShowForm = useAppStore((state) => state.setShowForm);
  const formRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const postscribeLoadedRef = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (showForm && !dialog.open) {
      dialog.showModal();
    } else if (!showForm && dialog.open) {
      dialog.close();
    }
  }, [showForm]);

  useEffect(() => {
    if (!showForm) {
      postscribeLoadedRef.current = false;
      return;
    }

    if (!formRef.current || formRef.current.innerHTML || postscribeLoadedRef.current) {
      return;
    }

    postscribeLoadedRef.current = true;
    postscribe(
      formRef.current,
      '<script src="https://portlandtrailblazers.formstack.com/forms/js.php/rip_city_rewards"></script>',
      {
        error: (err: Error) => {
          return console.log("Error Loading Formstack", err);
        },
      },
    );
  }, [showForm]);

  const handleOpen = () => {
    posthog?.capture("contact_form_opened");
    setShowForm(true);
  };

  const handleDialogClose = () => {
    if (!showForm) {
      return;
    }

    posthog?.capture("contact_form_closed");
    setShowForm(false);
  };

  const handleCloseClick = () => {
    dialogRef.current?.close();
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) {
      dialogRef.current?.close();
    }
  };

  return (
    <>
      <div className="block w-full max-w-none">
        <Cta onOpen={handleOpen} />
      </div>

      <dialog
        ref={dialogRef}
        className="m-auto w-[calc(100%-2rem)] max-w-[750px] border-0 bg-transparent p-0 text-inherit backdrop:bg-black/50 [&:not([open])]:hidden"
        onClose={handleDialogClose}
        onClick={handleBackdropClick}
        aria-labelledby="contact-form-title"
      >
        <div className="relative rounded-sm bg-white p-6 shadow-[0_20px_25px_-5px_rgb(0_0_0/0.1),0_8px_10px_-6px_rgb(0_0_0/0.1)]">
          <button
            type="button"
            className="absolute top-4 right-4 flex size-8 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-grey-dark hover:bg-grey-light hover:text-dark"
            onClick={handleCloseClick}
            aria-label="Close contact form"
          >
            <AiOutlineClose aria-hidden />
          </button>

          <header className="">
            <h2 id="contact-form-title" className="font-sans text-xl font-semibold text-dark">
              Contact Us
            </h2>
          </header>

          <div className="mt-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <FormInstance formRef={formRef} />
          </div>
        </div>
      </dialog>
    </>
  );
});

type TFormCta = {
  onOpen: () => void;
};

const Cta: FC<TFormCta> = ({ onOpen }) => {
  return (
    <div className="box-border flex w-full flex-col items-stretch gap-4 pt-6 text-center tablet:flex-row tablet:items-center tablet:justify-between tablet:text-left">
      <div className="flex min-w-0 flex-col items-center space-x-2 tablet:flex-row tablet:items-center">
        <div
          className="flex size-12 shrink-0 items-center justify-center rounded-full text-4xl text-magenta"
          aria-hidden
        >
          <FaUniversalAccess />
        </div>
        <div>
          <h2 className="font-sans text-xl font-bold leading-tight text-dark">Need assistance?</h2>
          <p className="m-0 font-sans text-base leading-snug text-grey-dark">
            Our team is here to help.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpen}
        className="w-full shrink-0 cursor-pointer rounded border border-magenta bg-transparent px-5 py-3 font-sans text-sm font-bold tracking-wide uppercase text-magenta transition-colors duration-200 hover:bg-white tablet:w-auto"
      >
        Contact Us
      </button>
    </div>
  );
};

export const FormInstance = ({ formRef }: { formRef: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <div
      id="form"
      className="mx-auto max-w-[750px] rounded-md bg-white max-[767px]:py-[15px]"
      ref={formRef}
    />
  );
};

export default Form;
